import clsx from 'clsx';
import { Disclosure, Transition } from '@headlessui/react';
import {
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import { Button, Typography } from '@/components/common';
import { Minus, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { InputRadio } from '@/components/form';
import { PaymentType } from '@/interfaces';
import { OptionType } from '@/components/screens/dashboard/settings/PaymentDetails';

interface Props {
	className?: string;
	errors: FieldErrors<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	options: OptionType[];
	onClickSave: () => void;
	onClickCancel: () => void;
	paymentSelected: PaymentType | null;
	setPaymentSelected: (value: PaymentType) => void;
	setPaymentToDelete: (value: PaymentType) => void;
	showDeleteModal: () => void;
	setSelectedIndex: (value: number) => void;
	selectedIndex: number;
}

export const PaymentDropdown: React.FC<Props> = ({
	className,
	errors,
	watch,
	register,
	setValue,
	options,
	onClickSave,
	onClickCancel,
	paymentSelected,
	setPaymentSelected,
	setPaymentToDelete,
	showDeleteModal,
	setSelectedIndex,
	selectedIndex,
}) => {
	const [disabledButtons, setDisabedButtons] = useState<boolean>(true);

	const handleToggle = (index: number): void => {
		setSelectedIndex(selectedIndex === index ? -1 : index);
	};

	// check the empty forms to put disable the buttons
	useEffect(() => {
		if (selectedIndex !== -1) {
			let isEmpty = !options[selectedIndex]?.isAdded;
			const type = options[selectedIndex].name;
			if (type === 'paypal' && watch('paypalEmail')) {
				isEmpty = false;
			} else if (
				type === 'venmo' &&
				watch('accountName') &&
				watch('accountAlias') &&
				watch('phoneNumber')
			) {
				isEmpty = false;
			} else {
				isEmpty = true;
			}
			setDisabedButtons(isEmpty);
		} else {
			setDisabedButtons(true);
		}
	}, [
		selectedIndex,
		watch('paypalEmail'),
		watch('accountName'),
		watch('accountAlias'),
		watch('phoneNumber'),
	]);

	return (
		<div className={clsx('mt-6', className)}>
			<Typography
				type="semibold-18"
				className="mb-6 !font-medium lg:!font-semibold"
			>
				Payment Preferences
			</Typography>
			{options.map((option, index) => (
				<div key={index} className="mb-4">
					<Disclosure as={'div'}>
						{() => {
							const Component = option.component;
							return (
								<>
									<Disclosure.Button
										onClick={() => handleToggle(index)}
										className={clsx(
											'bg-white pt-4 px-4 !outline-none w-full flex items-center transition-all duration-200',
											{
												'rounded-3xl border border-[#0D061633] pb-4':
													index !== selectedIndex,
												'rounded-t-3xl border-t border-x border-green-base pb-6':
													index === selectedIndex,
											}
										)}
									>
										<div className="flex items-center justify-between w-full">
											<div className="flex flex-1 items-center">
												{option.isAdded && (
													<InputRadio
														name="payment"
														register={register}
														onChange={(e) => {
															const value = e.target.value as PaymentType;
															setPaymentSelected(value);
														}}
														value={option.name}
														defaultChecked={option.name === paymentSelected}
													/>
												)}
												<div className="flex items-center gap-2">
													<div>{option.icon}</div>
													<Typography type="semibold-16">
														{option.label}
													</Typography>
												</div>
											</div>
											{index === selectedIndex ? <Minus /> : <Plus />}
										</div>
									</Disclosure.Button>
									<Transition
										show={index === selectedIndex}
										enter="transition duration-200 ease-out"
										enterFrom="transform -translate-y-5 opacity-0"
										enterTo="transform translate-y-0 opacity-100"
										leave="transition duration-150 ease-in"
										leaveFrom="transform translate-y-0 opacity-100"
										leaveTo="transform -translate-y-5 opacity-0"
									>
										<Disclosure.Panel
											className={clsx(
												'bg-white px-4 pb-4 rounded-b-3xl space-y-4 border-x border-b border-green-base'
											)}
										>
											<Component
												errors={errors}
												watch={watch}
												register={register}
												setValue={setValue}
											/>
											<div className="flex flex-col md:flex-row items-center justify-end gap-2 !mt-8 md:mt-0">
												{option.isAdded && (
													<Button
														decoration="danger"
														size="medium"
														onClick={() => {
															setPaymentToDelete(option.name);
															showDeleteModal();
														}}
														label="Delete"
														className="w-full md:w-auto "
													/>
												)}
												<Button
													decoration="secondary"
													size="medium"
													onClick={onClickCancel}
													label="Cancel"
													disabled={disabledButtons}
													className="w-full md:w-auto"
												/>
												<Button
													decoration="primary"
													size="medium"
													onClick={onClickSave}
													label="Save"
													disabled={disabledButtons}
													className="w-full md:w-auto"
												/>
											</div>
										</Disclosure.Panel>
									</Transition>
								</>
							);
						}}
					</Disclosure>
				</div>
			))}
		</div>
	);
};
